import {Component, h} from 'preact'
import Portal from 'preact-portal'
import tinycolor from 'tinycolor2'
import TextButton from '../ui-components/TextButton'
import Icon from '../assets'
import Button from '../ui-components/Button'
import Colors from './Colors'
import Buttons from './Buttons'
import Cards from './Cards'
import CustomCss from './CustomCss'
import {getColors, getButtons, getCards, getFonts} from './DefaultSettings'
import Axios, {AxiosResponse} from 'axios'
import Fonts from './Fonts'
import AlertComponent from '../ui-components/AlertComponent'
import DropdownControl from '../dropdown/DropdownControl'
import qs from 'qs'
import globals from '../../utils/globals'
import MappedVariables from './MappedVariables'

type ThemeTypes = null | 'color' | 'button' | 'font' | 'card' | 'customCss'

export type StylingConfigType = {
  [key: string]: string | null
}

interface Props {
  theme: ThemeTypes
}

interface State {
  currentTheme: ThemeTypes,
  cards: Array<Object>,
  fonts: Array<Object>,
  searchParams: string,

  defaultColorsStyling: StylingConfigType,
  defaultCardsStyling: StylingConfigType,
  defaultButtonsStyling: StylingConfigType,
  defaultFontsStyling: StylingConfigType,

  existingColorsStyling: StylingConfigType,
  existingButtonsStyling: StylingConfigType,
  existingCardsStyling: StylingConfigType,
  existingFontsStyling: StylingConfigType,

  updatedColorsStyling: StylingConfigType,
  updatedButtonsStyling: StylingConfigType,
  updatedCardsStyling: StylingConfigType,
  updatedFontsStyling: StylingConfigType,

  existingStyling: StylingConfigType,
  updatedStyling: StylingConfigType,
  isLoading: boolean,
  existingCustomCss: string,
  updatedCustomCss: null | string,
  errorMessage: string
}

interface CustomCssResponse {
  customcss: string
}

const THEME_LIST: Array<{
  type: ThemeTypes,
  title: string
}> = [
  {
    type: 'color',
    title: 'Colors'
  },
  {
    type: 'button',
    title: 'Buttons'
  },
  {
    type: 'font',
    title: 'Fonts'
  },
  {
    type: 'card',
    title: 'Cards'
  },
  {
    type: 'customCss',
    title: 'Custom CSS'
  }
]

export default class ThemeConfigurator extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      currentTheme: props.theme,
      cards: [],
      fonts: [],
      isLoading: false,
      errorMessage: '',
      searchParams: '',

      defaultButtonsStyling: {},
      defaultColorsStyling: {},
      defaultFontsStyling: {},
      defaultCardsStyling: {},

      existingButtonsStyling: {},
      existingColorsStyling: {},
      existingFontsStyling: {},
      existingCardsStyling: {},

      updatedButtonsStyling: {},
      updatedColorsStyling: {},
      updatedFontsStyling: {},
      updatedCardsStyling: {},

      existingStyling: {},
      updatedStyling: {},
      existingCustomCss: '',
      updatedCustomCss: null
    }

    this.getComponent = this.getComponent.bind(this)
    this.discardChanges = this.discardChanges.bind(this)
    this.normalizeThemes = this.normalizeThemes.bind(this)
    this.updateCommunityStyling = this.updateCommunityStyling.bind(this)
    this.onSave = this.onSave.bind(this)
    this.clearLocalStorage = this.clearLocalStorage.bind(this)
    this.setConfigurationURL = this.setConfigurationURL.bind(this)
    this.getSearchParameters = this.getSearchParameters.bind(this)
  }

  CHUNK_SIZE: number = 15

  normalizeThemes () {
    const isCustomCssModuleEnabled = ((globals('activeModules') || []).indexOf('customCss') !== -1)
    if (!isCustomCssModuleEnabled) {
      const customCss = THEME_LIST.findIndex(theme => theme.type === 'customCss')
      if (customCss > 0) {
        THEME_LIST.splice(customCss, 1)
      }
    }

    return THEME_LIST.map(theme => ({
      title: theme.title,
      onClick: () => {
        this.setConfigurationURL(theme.type)
        this.setState({ currentTheme: theme.type })
      }
    }))
  }

  setConfigurationURL (currentTheme: ThemeTypes) {
    const queryParameters = this.getSearchParameters()

    this.setState({ searchParams: window.location.search })
    queryParameters.theme = currentTheme

    history.pushState('', '', `?${qs.stringify(queryParameters)}`)
  }

  getComponent () {
    const currentTheme = this.state.currentTheme
    switch (currentTheme) {
      case 'color':
        return <Colors
          defaultStyling={this.state.defaultColorsStyling}
          updatedStyling={this.state.updatedColorsStyling}
          onUpdate={(updatedColorsStyling: StylingConfigType) => {
            this.setState({ updatedColorsStyling: updatedColorsStyling })
            this.updateCommunityStyling(updatedColorsStyling)
          }}
        />

      case 'button':
        return <Buttons
          defaultStyling={this.state.defaultButtonsStyling}
          updatedStyling={this.state.updatedButtonsStyling}
          onUpdate={(updatedButtonsStyling: StylingConfigType) => {
            this.setState({ updatedButtonsStyling: updatedButtonsStyling })
            this.updateCommunityStyling(updatedButtonsStyling)
          }}
        />

      case 'font':
        return <Fonts
          defaultStyling={this.state.defaultFontsStyling}
          updatedStyling={this.state.updatedFontsStyling}
          onUpdate={(updatedFontsStyling: StylingConfigType) => {
            this.setState({
              updatedFontsStyling: updatedFontsStyling
            })
            this.updateCommunityStyling(updatedFontsStyling)
          }}
        />

      case 'card':
        return <Cards
          defaultStyling={this.state.defaultCardsStyling}
          updatedStyling={this.state.updatedCardsStyling}
          updatedColors={this.state.updatedColorsStyling}
          onUpdate={(updatedCardsStyling: StylingConfigType) => {
            this.setState({ updatedCardsStyling: updatedCardsStyling })
            this.updateCommunityStyling(updatedCardsStyling)
          }}
        />
      case 'customCss':
        if (typeof this.state.updatedCustomCss !== 'string') {
          return null
        }
        return <CustomCss
          content={this.state.updatedCustomCss as string}
          onUpdate={(updatedCustomCss: string) => this.setState({ updatedCustomCss: updatedCustomCss })}
        />
      default:
        return null
    }
  }

  getSearchParameters () {
    const searchParams = window.location.search
    return qs.parse(searchParams, { ignoreQueryPrefix: true })
  }

  discardChanges () {
    if (this.state.currentTheme === 'color') {
      this.setState({ updatedColorsStyling: this.state.existingColorsStyling }, () => this.updateCommunityStyling(this.state.existingColorsStyling))
    } else if (this.state.currentTheme === 'button') {
      this.setState({ updatedButtonsStyling: this.state.existingButtonsStyling }, () => this.updateCommunityStyling(this.state.existingButtonsStyling))
    } else if (this.state.currentTheme === 'card') {
      this.setState({ updatedCardsStyling: this.state.existingCardsStyling }, () => this.updateCommunityStyling(this.state.existingCardsStyling))
    } else if (this.state.currentTheme === 'customCss') {
      this.setState({ updatedCustomCss: this.state.existingCustomCss })
    }

    const queryParameters = this.getSearchParameters()
    delete queryParameters.theme

    this.setState({ currentTheme: null, errorMessage: '' },
      () => window.history.pushState('', '', `?${qs.stringify(queryParameters)}`))
  }

  updateCommunityStyling (newStyling: StylingConfigType) {
    this.setState({
      updatedStyling: {
        ...this.state.updatedStyling,
        ...this.state.updatedColorsStyling,
        ...this.state.updatedButtonsStyling,
        ...this.state.updatedFontsStyling,
        ...this.state.updatedCardsStyling,
        ...newStyling
      }
    })
  }

  async onSave () {
    this.setState({ isLoading: true })
    try {
      if (this.state.currentTheme !== 'customCss') {
        let updatedStyling: StylingConfigType = Object.assign({}, this.state.updatedStyling)
        Object.keys(MappedVariables).forEach(variable => {
          if (typeof MappedVariables[variable] === 'string') {
            const name = MappedVariables[variable] as string
            updatedStyling[variable] = this.state.updatedStyling[name]
          } else {
            const variableObj = MappedVariables[variable] as {
              name: string,
              alpha?: number,
              darken?: number,
              brighten?: number
            }

            if (variableObj.alpha) {
              updatedStyling[variable] = this.state.updatedStyling[name] = tinycolor(this.state.updatedStyling[variableObj.name]).setAlpha(variableObj.alpha).toHex8String()
            } else if (variableObj.darken) {
              updatedStyling[variable] = this.state.updatedStyling[name] = tinycolor(this.state.updatedStyling[variableObj.name]).darken(variableObj.darken).toHex8String()
            } else if (variableObj.brighten) {
              updatedStyling[variable] = this.state.updatedStyling[name] = tinycolor(this.state.updatedStyling[variableObj.name]).brighten(variableObj.brighten).toHex8String()
            }
          }
        })
        await Axios.post('/branding/setVariables', {
          ...updatedStyling
        })
      } else {
        await Axios.post('/branding/updateCustomCss', {
          data: this.state.updatedCustomCss
        })
      }

      this.clearLocalStorage()
      const queryParameters = this.getSearchParameters()
      delete queryParameters.theme
      window.location.search = qs.stringify(queryParameters)

    } catch (error) {
      let errorMessage: string = ''

      if (error.response.data.errors && error.response.data.errors.customCss) {
        errorMessage = error.response.data.errors.customCss.reduce((accum:string, item:string) => {
          accum += '<br>' + item
          return accum
        })
      } else {
        errorMessage = 'Something went wrong. Please try again.'
      }

      this.setState({
        errorMessage: errorMessage
      })
      this.setState({ isLoading: false })
    }
  }

  clearLocalStorage () {
    sessionStorage.removeItem('themeConfiguration')
  }

  componentDidUpdate (previousProps: Readonly<Props>, previousState: Readonly<State>): void {
    if (this.state.currentTheme) {
      sessionStorage.setItem('themeConfiguration', JSON.stringify(this.state))
    } else {
      this.clearLocalStorage()
    }

    const bodyElem = document.body
    if (this.state.currentTheme && this.state.currentTheme !== previousState.currentTheme && this.getComponent() !== null) {
      bodyElem.classList.add('theme-configuration-mode')
    } else if (previousState.currentTheme && !this.state.currentTheme) {
      bodyElem.classList.remove('theme-configuration-mode')
    }
  }

  async componentDidMount () {
    if (this.state.currentTheme !== null) {
      const bodyElem = document.body
      bodyElem.classList.add('theme-configuration-mode')
    }
    const configurationData: State = JSON.parse(sessionStorage.getItem('themeConfiguration') || '{}')

    if (configurationData.currentTheme) {
      this.setState({ ...configurationData })
      this.setConfigurationURL(configurationData.currentTheme)
      return
    }

    let existingStyling: AxiosResponse<StylingConfigType> = await Axios.get('/branding/variables')
    let existingCustomCss: AxiosResponse<CustomCssResponse> = await Axios.get('/branding/customcss?raw=1')

    if (!existingStyling.data['config-button-border-width']) {
      existingStyling.data['config-button-border-width'] = existingStyling.data['config-button-cta-border-width']
    }
    if (!existingStyling.data['config-button-border-radius']) {
      existingStyling.data['config-button-border-radius'] = existingStyling.data['config-button-cta-border-radius']
    }

    // COLORS
    let defaultColorsStyling: StylingConfigType = getColors()

    const existingColorsStyling: StylingConfigType = {}

    Object.keys(defaultColorsStyling).forEach(key => {
      const color = existingStyling.data[key] || defaultColorsStyling[key]
      existingColorsStyling[key] = color !== null ? tinycolor(existingStyling.data[key] || defaultColorsStyling[key]).toHex8String() : color
    })

    // BUTTONS
    let defaultButtonsStyling: StylingConfigType = getButtons(existingStyling.data)

    const existingButtonsStyling: StylingConfigType = {}

    Object.keys(defaultButtonsStyling).forEach(key => {
      if (key === 'config--main-button-base-font-weight' && (existingStyling.data[key] || defaultButtonsStyling[key]) === 'normal') {
        existingButtonsStyling[key] = '400'
      } else {
        existingButtonsStyling[key] = existingStyling.data[key] || defaultButtonsStyling[key]
      }
    })

    // CARDS
    let defaultCardsStyling: StylingConfigType = getCards()

    const existingCardsStyling: StylingConfigType = {}

    Object.keys(defaultCardsStyling).forEach(key => {
      existingCardsStyling[key] = existingStyling.data[key] || defaultCardsStyling[key]
    })

    // FONTS
    let defaultFontsStyling: StylingConfigType = getFonts()

    const existingFontsStyling: StylingConfigType = {}

    Object.keys(defaultFontsStyling).forEach(key => {
      existingFontsStyling[key] = existingStyling.data[key] || defaultFontsStyling[key]
    })

    this.setState({
      defaultButtonsStyling: defaultButtonsStyling,
      updatedButtonsStyling: existingButtonsStyling,
      existingButtonsStyling: existingButtonsStyling,

      defaultColorsStyling: defaultColorsStyling,
      updatedColorsStyling: existingColorsStyling,
      existingColorsStyling: existingColorsStyling,

      defaultCardsStyling: defaultCardsStyling,
      updatedCardsStyling: existingCardsStyling,
      existingCardsStyling: existingCardsStyling,

      defaultFontsStyling: defaultFontsStyling,
      updatedFontsStyling: existingFontsStyling,
      existingFontsStyling: existingFontsStyling,

      existingStyling: existingStyling.data,
      updatedStyling: existingStyling.data,
      existingCustomCss: existingCustomCss.data.customcss,
      updatedCustomCss: existingCustomCss.data.customcss
    }, () => sessionStorage.setItem('themeConfiguration', JSON.stringify(this.state)))
  }

  render (_: Props, { currentTheme, isLoading, updatedStyling, updatedCustomCss, errorMessage }: State) {
    const component = this.getComponent()

    if (!currentTheme || component === null) {
      return (
        <DropdownControl
          displayListUpwards
          align='adjust'
          triggerClass='theme-dropdown'
          trigger={<Button class='theme-btn' insidedPurple small><span>Theme</span> <Icon size={12} color='#2B3346' icon='chevronUp' /></Button>}
          items={this.normalizeThemes()}
        />
      )
    }

    let latestCommunityStyle: Array<string> = Object.keys(updatedStyling)
      .filter(configKey => updatedStyling[configKey])
      .map(configKey => {
        return `--${configKey}: ${updatedStyling[configKey]}`
      })

    return <Portal into='body'>
      <div className='theme-configurator'>
        <style>
          {`
            html {
              ${latestCommunityStyle.join(';')}
            }
            ${updatedCustomCss}
          `}
        </style>
        <div className='theme-configurator--header'>
          <div className='theme-configurator--header__left'>
            <h4 className='theme-configurator--header-title'>{ THEME_LIST.filter(theme => theme.type === currentTheme)[0].title }</h4>
          </div>
          <TextButton style={{ padding: '4px', display: 'flex', borderRadius: '2px' }} class='close-btn ' onClick={this.discardChanges} >
            <Icon icon='close' />
          </TextButton>
        </div>
        <div className={`theme-configurator--body`}>
          { this.getComponent() }
        </div>
        <div class='theme-configurator--footer'>
          {errorMessage !== '' && <AlertComponent
            messageType='error'
            message={errorMessage}
            renderHTML
            onClose={() => this.setState({ errorMessage: '' })}
          />}
          <div className='theme-configurator--buttons'>
            <Button insided loading={isLoading} onClick={this.onSave}>Publish</Button>
            <Button insidedSecondary onClick={this.discardChanges}>Cancel</Button>
          </div>
        </div>
      </div>
    </Portal>
  }
}
